import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { apiClient } from "../../services/api";
import { LEAD_DISPATCH_ERROR_MESSAGES } from "../../services/shared";
import { RuleType, SimpleRule } from "../../types";
import SimpleRuleForm, {
  SimpleRuleFormData,
  simpleFormDataToPayload,
  simpleRuleToFormData,
} from "./SimpleRuleForm";
import DispatcherRuleForm, {
  DispatcherRuleFormData,
  dispatcherFormDataToPayload,
} from "./DispatcherRuleForm";

type Step = "choose-type" | "fill-form";

const withCopySuffix = (name: string): string => {
  const suffix = " (Copia)";
  const max = 100;
  const base =
    name.length + suffix.length > max
      ? name.slice(0, max - suffix.length)
      : name;
  return base + suffix;
};

export default function RuleCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateFrom = (
    location.state as { duplicateFrom?: SimpleRule } | null
  )?.duplicateFrom;
  const isDuplicate = !!duplicateFrom;

  const [step, setStep] = useState<Step>(
    isDuplicate ? "fill-form" : "choose-type"
  );
  const [ruleType, setRuleType] = useState<RuleType>("simple");
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const duplicateDefaults: SimpleRuleFormData | undefined = useMemo(() => {
    if (!duplicateFrom) return undefined;
    const base = simpleRuleToFormData(duplicateFrom);
    return {
      ...base,
      displayName: withCopySuffix(base.displayName),
      active: false,
    };
  }, [duplicateFrom]);

  const handleCreate = async (
    payload: Record<string, unknown>
  ): Promise<void> => {
    setIsLoading(true);
    setSubmitError(null);
    try {
      const res = await apiClient.post(
        "/v1/lead-dispatch/rules",
        payload
      );
      const created = res.data?.data;
      if (created?.id) {
        navigate(`/lead-dispatch/rules/${created.id}`);
      } else {
        navigate("/lead-dispatch/rules");
      }
    } catch (e) {
      console.error(e);
      if (axios.isAxiosError(e)) {
        const data = e.response?.data;
        const errCode = data?.error;
        if (errCode && LEAD_DISPATCH_ERROR_MESSAGES[errCode]) {
          setSubmitError(LEAD_DISPATCH_ERROR_MESSAGES[errCode]);
        } else if (data?.issues?.length) {
          setSubmitError(
            data.issues
              .map(
                (i: { path: string[]; message: string }) =>
                  `${i.path.join(".")}: ${i.message}`
              )
              .join(" — ")
          );
        } else {
          setSubmitError("Errore durante la creazione della regola");
        }
      } else {
        setSubmitError("Errore durante la creazione della regola");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSimpleSubmit = async (data: SimpleRuleFormData) => {
    await handleCreate(simpleFormDataToPayload(data));
  };

  const handleDispatcherSubmit = async (data: DispatcherRuleFormData) => {
    await handleCreate(dispatcherFormDataToPayload(data));
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", py: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        {isDuplicate
          ? "Duplica regola Lead Dispatching"
          : "Nuova regola Lead Dispatching"}
      </Typography>

      {step === "choose-type" && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Tipo di regola
          </Typography>
          <FormControl>
            <RadioGroup
              value={ruleType}
              onChange={(e) => setRuleType(e.target.value as RuleType)}
            >
              <FormControlLabel
                value="simple"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Simple
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Destinazione singola: URL, headers, mapping e extra
                      fields. Le lead in ingresso vengono inoltrate 1:1.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="dispatcher"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Dispatcher
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Aggregatore di simple rule con schedule per giorno e
                      fascia oraria. Distribuisce le lead in base alle
                      percentuali configurate.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/lead-dispatch/rules")}
            >
              Annulla
            </Button>
            <Button
              variant="contained"
              onClick={() => setStep("fill-form")}
            >
              Continua
            </Button>
          </Stack>
        </Paper>
      )}

      {step === "fill-form" && (
        <Paper sx={{ p: 3 }}>
          {isDuplicate ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Stai duplicando la regola{" "}
              <strong>{duplicateFrom?.displayName}</strong> — il nuovo record
              verrà creato come copia disattivata. Modifica i campi necessari
              (URL, credenziali, …) prima di salvare.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              Stai creando una regola di tipo <strong>{ruleType}</strong>.{" "}
              <Button
                size="small"
                onClick={() => {
                  setStep("choose-type");
                  setSubmitError(null);
                }}
              >
                Cambia tipo
              </Button>
            </Alert>
          )}
          {ruleType === "simple" ? (
            <SimpleRuleForm
              defaultValues={duplicateDefaults}
              onSubmit={handleSimpleSubmit}
              isLoading={isLoading}
              submitLabel={isDuplicate ? "Crea copia" : "Crea regola"}
              submitError={submitError}
            />
          ) : (
            <DispatcherRuleForm
              onSubmit={handleDispatcherSubmit}
              isLoading={isLoading}
              submitLabel="Crea regola"
              submitError={submitError}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}
